import { useState } from 'react';

import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';


import { useSSOLoginMutation } from './useAuthMutations';

export const SsoModal = () => {
  const { t } = useTranslation(['auth', 'common']);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [companySlug, setCompanySlug] = useState<string>('');
  const ssoLogin = useSSOLoginMutation({
    onSuccess: (data) => {
      window.open(data.url!, '_blank');
    },
    onError: () => {
      toast({
        title: t('auth:email.loginNotFound.subject'),
        description: 'SSO start failed',
        status: 'error',
      });
    },
  });

  return (
    <>
      <Button
        color="gray.900"
        isDisabled={ssoLogin.isPending}
        onClick={onOpen}
        variant="outline"
      >
        {t('auth:login.ssoLogin')}
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent mx={4} borderRadius="xl" boxShadow="xl">
          <ModalHeader pb={2}>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="semibold" color="gray.900">
                {t('auth:login.ssoTitle')}
              </Text>
              <Text fontSize="sm" color="gray.600" fontWeight="normal">
                {t('auth:login.ssoDescription')}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton disabled={ssoLogin.isPending} top={4} right={4} />
          <ModalBody pt={2} pb={6}>
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="medium"
                color="gray.700"
                mb={2}
              >
                {t('auth:login.companySlug')}
              </FormLabel>
              <Input
                placeholder={t('auth:login.companySlugPlaceholder')}
                value={companySlug}
                onChange={(e) => setCompanySlug(e.target.value)}
                isDisabled={ssoLogin.isPending}
                size="lg"
                borderRadius="lg"
                borderColor="gray.200"
                _focus={{
                  borderColor: 'blue.400',
                  boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                }}
                _hover={{ borderColor: 'gray.300' }}
                bg="white"
              />
              <FormHelperText fontSize="xs" color="gray.500" mt={2}>
                {t('auth:login.companySlugDescription')}
              </FormHelperText>
            </FormControl>
          </ModalBody>
          <ModalFooter pt={0} gap={3}>
            <Button onClick={onClose} isDisabled={ssoLogin.isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="@primary"
              isLoading={ssoLogin.isPending}
              loadingText={t('auth:login.connecting')}
              onClick={() => {
                if (!companySlug?.trim()) {
                  toast({
                    title: 'Company identifier required',
                    description: t('auth:login.companySlugError'),
                    status: 'warning',
                  });
                  return;
                }
                ssoLogin.mutate({
                  companySlug: companySlug.trim(),
                });
              }}
            >
              {t('common:actions.continue')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
